import React, { useState } from 'react';
import { GripVertical, Layers, TrendingUp, X } from 'lucide-react';
import { toast } from 'sonner';
import { getFieldLabel } from '@/lib/fieldLabel';
import { FilterFieldPicker } from './FilterFieldPicker';

export function DropShelf({ label, icon: Icon, items, acceptType, onDrop, onRemove, onReorder, allFields, fieldOverrides, onFieldProperties, onPillRightClick, className: extraClass, requireCategory, coachTarget, guessedFields = [], pickerFields }) {
  const [dragOver, setDragOver] = useState(false);
  const [dropTarget, setDropTarget] = useState(null); // insertion slot (0..items.length) where a vertical line is drawn

  // When a shelf is category-restricted (e.g. a Geo Map Location shelf only
  // accepts Geographic fields), verify the dropped field's data_category.
  const catOk = (name) => {
    if (!requireCategory) return true;
    const f = (allFields || []).find(ff => ff.name === name);
    return String(f?.data_category || '').toLowerCase().startsWith(requireCategory);
  };
  const rejectCat = () => toast.error('This drop zone only accepts ' + requireCategory.charAt(0).toUpperCase() + requireCategory.slice(1) + ' fields');

  // Work out the insertion slot (0..items.length) from the pointer position,
  // row-aware so it still works when pills wrap onto multiple lines. We query
  // the live pill elements inside the shelf container rather than tracking
  // per-pill handlers, so the drop lands at the indicated line no matter
  // whether the pointer is released over a pill, the gap, or the indicator.
  const computeSlot = (container, x, y) => {
    const pills = Array.from(container.querySelectorAll('[data-testid^="pill-"]'));
    let slot = pills.length;
    for (let i = 0; i < pills.length; i++) {
      const r = pills[i].getBoundingClientRect();
      if (y >= r.top - 2 && y <= r.bottom + 2) {
        if (x < r.left + r.width / 2) { slot = i; break; }
        slot = i + 1;
      }
    }
    return slot;
  };

  const commitDrop = (data, slot) => {
    if (data.fromShelf === label && onReorder) {
      const sourceIndex = items.indexOf(data.name);
      if (sourceIndex === -1) return;
      const without = items.filter(x => x !== data.name);
      let insertAt = slot > sourceIndex ? slot - 1 : slot;
      if (insertAt < 0) insertAt = 0;
      if (insertAt > without.length) insertAt = without.length;
      without.splice(insertAt, 0, data.name);
      onReorder(without);
    } else if (data.fromShelf) {
      // Cross-shelf move — always accept (the parent handler removes from old shelf)
      if (!catOk(data.name)) { rejectCat(); return; }
      onDrop(data.name, data.type, data.entity_id, slot);
    } else if (acceptType === 'any' || data.type === acceptType) {
      // New field from the field list
      if (!catOk(data.name)) { rejectCat(); return; }
      onDrop(data.name, data.type, data.entity_id, slot);
    } else {
      toast.error('This shelf only accepts ' + acceptType + 's');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    setDragOver(true);
    setDropTarget(computeSlot(e.currentTarget, e.clientX, e.clientY));
  };
  const handleDragLeave = (e) => {
    // Only clear when the pointer actually leaves the shelf, not when it moves
    // between the pills/indicator inside it.
    if (!e.currentTarget.contains(e.relatedTarget)) { setDragOver(false); setDropTarget(null); }
  };
  const handleDrop = (e) => {
    e.preventDefault();
    const slot = computeSlot(e.currentTarget, e.clientX, e.clientY);
    setDragOver(false);
    setDropTarget(null);
    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));
      commitDrop(data, slot);
    } catch {}
  };

  const isDim = acceptType === 'dimension' || acceptType === 'any';
  const getDisplayName = (fieldName) => { const ovr = fieldOverrides && fieldOverrides[fieldName]; const f = allFields && allFields.find(ff => ff.name === fieldName); return getFieldLabel(f, fieldName, ovr); };

  return (
    <div onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} className={'rounded-xl border-2 border-dashed transition-all min-h-[44px] p-2 ' + (dragOver ? 'border-primary/60 bg-primary/5' : 'border-border/30 bg-muted/10 hover:border-border/50') + ' ' + (extraClass || '')} data-testid={'shelf-' + label.toLowerCase().replace(/[^a-z]/g, '')} data-coach-target={coachTarget}>
      <div className="flex items-center justify-between gap-1.5 mb-1">
        <div className="flex items-center gap-1.5"><Icon className="w-3 h-3 text-muted-foreground" /><span className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold">{label}</span></div>
        {pickerFields && (
          <FilterFieldPicker
            dims={pickerFields}
            placeholder="+ Add"
            testid={'shelf-add-' + label.toLowerCase().replace(/[^a-z]/g, '')}
            triggerClassName="h-5 px-1.5 text-[9px] rounded-md bg-muted/40 border border-border/40 flex items-center gap-1 text-muted-foreground hover:border-primary/50 hover:text-foreground transition-colors"
            onPick={(name) => { const f = (pickerFields || []).find(x => x.name === name); onDrop(name, acceptType, f?.entity_id); }}
          />
        )}
      </div>
      <div className="flex flex-wrap gap-1 items-stretch">
        {items.map((name, index) => {
          const bg = isDim ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20' : 'bg-primary/15 text-primary border-primary/20';
          const isGuessed = guessedFields.includes(name) || guessedFields.includes(String(name).split('__')[0]);
          return (
            <React.Fragment key={name}>
              {dropTarget === index && (
                <span data-testid={'drop-indicator-' + label.toLowerCase().replace(/[^a-z]/g, '')} className="w-[3px] self-stretch min-h-[20px] rounded-full bg-primary shadow-[0_0_6px] shadow-primary/40 shrink-0 pointer-events-none" />
              )}
              <span draggable
                onDragStart={(e) => { e.dataTransfer.setData('application/json', JSON.stringify({ name, type: isDim ? 'dimension' : 'measure', fromShelf: label })); e.dataTransfer.effectAllowed = 'copyMove'; }}
                onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); if (onPillRightClick) onPillRightClick(e.clientX, e.clientY, name, label); }}
                className={'inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] border cursor-grab active:cursor-grabbing select-none transition-all ' + bg + ' group'}
                data-testid={'pill-' + name} title={isGuessed ? name + ' — auto-mapped from import, review before saving' : name}>
                <GripVertical className="w-2 h-2 text-current opacity-30" />
                {isGuessed && <span data-testid={'guessed-dot-' + name} className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" title="Auto-mapped guess — double-check this field" />}
                {isDim ? <Layers className="w-2.5 h-2.5" /> : <TrendingUp className="w-2.5 h-2.5" />}
                {getDisplayName(name)}
                <button onClick={() => onRemove(name)} className="opacity-40 group-hover:opacity-100 hover:text-destructive transition-opacity"><X className="w-2.5 h-2.5" /></button>
              </span>
            </React.Fragment>
          );
        })}
        {dropTarget === items.length && items.length > 0 && (
          <span data-testid={'drop-indicator-' + label.toLowerCase().replace(/[^a-z]/g, '')} className="w-[3px] self-stretch min-h-[20px] rounded-full bg-primary shadow-[0_0_6px] shadow-primary/40 shrink-0 pointer-events-none" />
        )}
        {items.length === 0 && <span className="text-[9px] text-muted-foreground/30 italic py-0.5">Drag fields here</span>}
      </div>
    </div>
  );
}
